/**
 * Brands (Figma 09 · Brands 01–06). Rename, logo and delete are Owner only
 * here, as the design says (the backend also lets Contributors rename and
 * delete). Not built, no backend yet: Archive / Restore and the "Archived"
 * group, so Delete is offered straight away behind the typed name; and
 * "This month: N planned".
 */
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQueries } from "@tanstack/react-query";
import { ArrowRight, CalendarX, Trash2, TriangleAlert, Users } from "lucide-react";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useGetBrands, type Brand } from "@/hooks/useGetBrands";
import { useRenameBrand } from "@/hooks/useRenameBrand";
import { useDeleteBrand } from "@/hooks/useDeleteBrand";
import { BRAND_LOGO_ACCEPT, useUploadBrandLogo } from "@/hooks/useUploadBrandLogo";
import type { BrandGuests } from "@/hooks/useBrandGuests";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { KitButton } from "../../kit/Button";
import { KitInput } from "../../kit/Input";
import { BrandMark } from "../../kit/BrandPicker";
import { EmptyState, ErrorState } from "../../ViewStates";
import {
  ConfirmDialog,
  DetailCard,
  DetailHead,
  Fact,
  Facts,
  IndexCard,
  IndexRow,
  PageHead,
  QuietNote,
  SectionHead,
  SettingsPage,
  Split,
  SplitLoading,
  TextAction,
} from "../parts/SettingsParts";
import { useWorkspaceHead } from "../parts/scope";
import { plural } from "../parts/settingsFormat";
import { summarise, useBrandsConnections } from "./AccountsPage";

const NAME_MAX = 40;

function useBrandsGuests(brands: Brand[], enabled: boolean) {
  return useQueries({
    queries: brands.map((brand) => ({
      queryKey: ["brandGuests", brand.id] as const,
      queryFn: async () => {
        const res = await axiosInstance.get<BrandGuests>(`/brand/${brand.id}/guests`);
        return res.data;
      },
      enabled,
    })),
  });
}

export default function BrandsPage() {
  const head = useWorkspaceHead();
  const navigate = useNavigate();
  const { selectedOrganization } = useCurrentUser();
  const role = selectedOrganization?.role;
  const isOwner = role === "Owner";
  const canSeeClients = role === "Owner" || role === "Contributor";
  const setSelectedBrandId = useWorkspaceStore((s) => s.setSelectedBrandId);
  const brandsQuery = useGetBrands();
  const brands = brandsQuery.data ?? [];
  const connections = useBrandsConnections(brands);
  const guests = useBrandsGuests(brands, canSeeClients);
  const rename = useRenameBrand();
  const deleteBrand = useDeleteBrand();
  const [picked, setPicked] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Brand | null>(null);

  const selected = brands.find((b) => b.id === picked) ?? brands[0];
  const index = selected ? brands.indexOf(selected) : -1;
  const orgName = selectedOrganization?.organization.name ?? "the workspace";

  const clientLine = (i: number) => {
    const data = guests[i]?.data;
    if (!data) return null;
    const n = data.guests.length;
    const waiting = data.invites.length;
    if (!n && !waiting) return null;
    return [n ? plural(n, "client") : null, waiting ? `${waiting} invited` : null].filter(Boolean).join(", ");
  };

  const openFor = (brand: Brand, page: string) => {
    setSelectedBrandId(brand.id);
    navigate(page.startsWith("/") ? page : `/dashboard/settings?page=${page}`);
  };

  return (
    <SettingsPage>
      <PageHead
        {...head}
        title="Brands"
        status={
          isOwner
            ? `Every brand in ${orgName}: its accounts, its clients and its calendar.`
            : "Only owners rename or delete brands."
        }
      />
      {brandsQuery.isLoading ? (
        <SplitLoading caption="Loading the brands" />
      ) : brandsQuery.isError ? (
        <ErrorState title="The brands did not load" body="Creo could not read them. Nothing has changed." onRetry={() => void brandsQuery.refetch()} />
      ) : brands.length === 0 ? (
        <EmptyState title="No brands yet" body="Make a brand from the brand picker in the rail." />
      ) : (
        <Split
          backLabel="Brands"
          index={
            <IndexCard>
              <p className="px-3 pb-1 pt-2.5 text-[12px] font-semibold text-co-muted">Brands · {brands.length}</p>
              {brands.map((brand, i) => {
                const s = summarise(connections[i]?.data);
                const clients = clientLine(i);
                return (
                  <IndexRow
                    key={brand.id}
                    lead={<BrandMark brand={brand} size={36} />}
                    title={brand.name}
                    sub={[s.line, clients].filter(Boolean).join(" · ")}
                    subTone={s.attention.length ? "alert" : "muted"}
                    trail={s.attention.length ? <TriangleAlert aria-hidden className="size-3.5 shrink-0 text-co-alert-ink" /> : undefined}
                    active={brand.id === selected?.id}
                    onPress={() => {
                      setPicked(brand.id);
                      setRenaming(null);
                    }}
                  />
                );
              })}
            </IndexCard>
          }
          detail={
            selected ? (
              <BrandDetail
                key={selected.id}
                brand={selected}
                orgName={orgName}
                isOwner={isOwner}
                accountsLine={summarise(connections[index]?.data).line}
                clientsLine={canSeeClients ? (clientLine(index) ?? "None yet") : null}
                renaming={renaming === selected.id}
                onRename={() => setRenaming(selected.id)}
                onRenameDone={(name) => {
                  setRenaming(null);
                  if (name && name !== selected.name) rename.mutate({ brandId: selected.id, name });
                }}
                onOpen={(page) => openFor(selected, page)}
                onDelete={() => setDeleteTarget(selected)}
              />
            ) : null
          }
        />
      )}

      {deleteTarget ? (
        <ConfirmDialog
          title={`Delete ${deleteTarget.name} for good?`}
          description="This cannot be undone."
          consequences={[
            { icon: CalendarX, text: "Its posts, briefs, comments and media go." },
            { icon: Users, text: "Its social accounts are disconnected, its clients lose access, and its memory goes." },
          ]}
          typeToConfirm={deleteTarget.name}
          confirmLabel="Delete for good"
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => {
            const target = deleteTarget;
            deleteBrand.mutate(target.id, {
              onSuccess: () => queue.add({ title: `${target.name} deleted`, variant: "success" }),
              onError: () => queue.add({ title: `Could not delete ${target.name}`, description: "Nothing was removed. Please try again.", variant: "error" }),
            });
            setPicked(null);
            setDeleteTarget(null);
          }}
        />
      ) : null}
    </SettingsPage>
  );
}

function BrandDetail({
  brand,
  orgName,
  isOwner,
  accountsLine,
  clientsLine,
  renaming,
  onRename,
  onRenameDone,
  onOpen,
  onDelete,
}: {
  brand: Brand;
  orgName: string;
  isOwner: boolean;
  accountsLine: string;
  clientsLine: string | null;
  renaming: boolean;
  onRename: () => void;
  onRenameDone: (name: string | null) => void;
  onOpen: (page: string) => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(brand.name);
  const upload = useUploadBrandLogo(brand.id);
  const fileRef = useRef<HTMLInputElement>(null);
  const trimmed = name.trim();

  return (
    <DetailCard>
      <DetailHead
        lead={<BrandMark brand={brand} size={56} />}
        title={brand.name}
        sub={`Brand in ${orgName}`}
        aside={isOwner && !renaming ? <KitButton variant="secondary" onClick={onRename}>Rename</KitButton> : null}
      />
      {renaming ? (
        <form
          className="flex w-full max-w-[460px] flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmed) onRenameDone(trimmed);
          }}
        >
          <KitInput label="Brand name" value={name} maxLength={NAME_MAX} onChange={(e) => setName(e.target.value)} autoFocus />
          <div className="flex items-center gap-3">
            <KitButton type="submit" isDisabled={!trimmed || trimmed === brand.name}>
              Save
            </KitButton>
            <KitButton variant="ghost" onClick={() => onRenameDone(null)}>
              Cancel
            </KitButton>
          </div>
          <QuietNote>Everyone on the brand and its clients see the new name.</QuietNote>
        </form>
      ) : null}
      <Facts>
        <Fact label="Accounts">{accountsLine}</Fact>
        {clientsLine ? <Fact label="Clients">{clientsLine}</Fact> : null}
      </Facts>
      <div className="flex flex-wrap gap-x-3">
        <TextAction icon={ArrowRight} onPress={() => onOpen("social-accounts")}>
          Social accounts
        </TextAction>
        {clientsLine ? (
          <TextAction icon={ArrowRight} onPress={() => onOpen("clients")}>
            Clients
          </TextAction>
        ) : null}
        <TextAction icon={ArrowRight} onPress={() => onOpen("calendar")}>
          Calendar
        </TextAction>
      </div>

      {isOwner ? (
        <>
          <SectionHead title="Logo" />
          <div className="flex items-center gap-4">
            <BrandMark brand={brand} size={40} />
            <KitButton variant="secondary" loading={upload.isPending} onClick={() => fileRef.current?.click()}>
              Change logo
            </KitButton>
            <input
              ref={fileRef}
              type="file"
              accept={BRAND_LOGO_ACCEPT}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                upload.mutate(file, {
                  onSuccess: () => queue.add({ title: `${brand.name}’s logo changed`, variant: "success" }),
                  onError: (err) =>
                    queue.add({
                      title: "Could not change the logo",
                      description: err instanceof Error ? err.message : "Please try again.",
                      variant: "error",
                    }),
                });
              }}
            />
          </div>
          <SectionHead title="Delete" />
          <QuietNote>Deleting removes the brand’s posts, briefs, memory and client access for good.</QuietNote>
          <TextAction icon={Trash2} tone="danger" onPress={onDelete}>
            Delete {brand.name} for good
          </TextAction>
        </>
      ) : null}
    </DetailCard>
  );
}
